import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="public--loop-video"
//
// A silent recording that loops while it is at least half on screen and
// rests on its poster otherwise. Reduced motion never starts it and offers
// the button at once; otherwise the button waits until the recording plays,
// so a still never wears a pause sign. The button says whether it should
// play: a recording someone paused stays paused, and one someone played
// while it was mostly off screen starts once it's back in view.
export default class extends Controller {
  static targets = ["video", "toggle"]

  connect() {
    this.motion = window.matchMedia("(prefers-reduced-motion: no-preference)")
    this.reset = this.reset.bind(this)
    document.addEventListener("turbo:before-cache", this.reset)
    this.toggleTarget.hidden = this.motion.matches
    this.wanted = this.motion.matches
    this.observer = new IntersectionObserver(([entry]) => {
      this.inView = entry.isIntersecting
      if (this.inView && this.wanted) this.play()
      else this.videoTarget.pause()
    }, { threshold: 0.5 })
    this.observer.observe(this.videoTarget)
  }

  disconnect() {
    this.observer.disconnect()
    document.removeEventListener("turbo:before-cache", this.reset)
    this.reset()
  }

  toggle() {
    this.wanted = this.videoTarget.paused
    if (this.wanted && this.inView) this.play()
    else this.videoTarget.pause()
  }

  // Scrolling away can pause a play that hasn't started yet; that rejection
  // is expected.
  play() {
    this.videoTarget.play().catch(error => { if (error.name !== "AbortError") throw error })
  }

  update() {
    const playing = !this.videoTarget.paused
    if (playing) this.toggleTarget.hidden = false
    this.element.classList.toggle("is-playing", playing)
    this.toggleTarget.setAttribute("aria-label", playing ? "Pause the recording" : "Play the recording")
  }

  reset() {
    this.videoTarget.pause()
    this.videoTarget.currentTime = 0
    this.wanted = this.motion.matches
  }
}
